import { getCategoryId, getRelationshipSlug, getSubCategoryId } from "@/components/blocks/ThreeColumnNewsGridBlock/getCategoryId";
import { fetchCategoryAndSubCategoryData } from "@/services/categoriesService";

type CategorySlugRef = { slug?: string } | undefined;

type BlockCategoryRef =
  | string
  | { id?: string; _id?: string; slug?: string }
  | null
  | undefined;

/** Placeholder path segment when a news article has no sub-category (keeps 3-segment routes valid). */
export const NEWS_NO_SUBCATEGORY_SEGMENT = "_";

export const getCategoryListUrl = (
  categoryId: CategorySlugRef,
  subCategoryId: CategorySlugRef,
  locale?: string,
): string | undefined => {
  const categorySlug = categoryId?.slug;
  if (!categorySlug) return undefined;

  const localePrefix = locale ? `/${locale}` : "";
  const subCategorySlug = subCategoryId?.slug;

  if (subCategorySlug) {
    return `${localePrefix}/${categorySlug}/${subCategorySlug}`;
  }

  return `${localePrefix}/${categorySlug}`;
};

export const getCategoryListUrlFromArticle = (
  article:
    | {
        categoryId?: CategorySlugRef;
        subCategoryId?: CategorySlugRef;
      }
    | undefined,
  locale?: string,
  useSubCategory = false,
): string | undefined => {
  if (!article?.categoryId?.slug) return undefined;

  return getCategoryListUrl(
    article.categoryId,
    useSubCategory ? article.subCategoryId : undefined,
    locale,
  );
};

export const getCategoryListUrlForBlock = async (
  block: {
    category?: BlockCategoryRef;
    subCategory?: BlockCategoryRef;
  },
  locale?: string,
  lang?: string,
): Promise<string | undefined> => {
  const categoryId = getCategoryId(block.category ?? undefined);
  const subCategoryId = block.subCategory
    ? getSubCategoryId(block.subCategory ?? undefined)
    : undefined;

  let categorySlug = getRelationshipSlug(block.category ?? undefined);
  let subCategorySlug = block.subCategory
    ? getRelationshipSlug(block.subCategory ?? undefined)
    : undefined;

  if (categoryId && (!categorySlug || (subCategoryId && !subCategorySlug))) {
    const categoryData = await fetchCategoryAndSubCategoryData({ id: categoryId, lang });

    categorySlug = categorySlug || categoryData?.data?.slug;

    if (subCategoryId && categoryData?.data?.subCategories) {
      const matchedSubCategory = categoryData.data.subCategories.find(
        (subCat: { id?: string; _id?: string; slug?: string }) =>
          (subCat.id || subCat._id) === subCategoryId,
      );

      subCategorySlug = subCategorySlug || matchedSubCategory?.slug;
    }
  }

  if (!categorySlug) return undefined;

  return getCategoryListUrl(
    { slug: categorySlug },
    block.subCategory ? { slug: subCategorySlug } : undefined,
    locale,
  );
};

export const getNewsDetailsUrl = (
  categoryId: { slug: string } | undefined,
  subCategoryId: { slug: string } | undefined,
  slug: string | undefined,
  locale?: string,
): string => {
  if (!slug || !categoryId || !categoryId.slug) {
    console.warn("Missing required slug or category slug");
    return "/404";
  }

  const categorySlug = categoryId.slug;
  const subCategorySlug = subCategoryId?.slug || NEWS_NO_SUBCATEGORY_SEGMENT;

  // Include locale prefix if provided
  const localePrefix = locale ? `/${locale}` : "";
  const url = `${localePrefix}/${categorySlug}/${subCategorySlug}/${slug}`;

  try {
    return decodeURI(url);
  } catch {
    return url;
  }
};
